import { useDispatch } from 'react-redux';
import { shopPrefix } from '../../config';
import { SOURCE_PRODUCT_GROUPS } from '../../store/constants';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { failSelectionlists } from '../../store/orderlist/actions';
import { useTranslation } from 'react-i18next';
import { http } from '../axios';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { nothingSelected } from '../../shared/product-selection/ProductSelectionUtils';

type AddToGroupBulk = {
    sourceProductGroupId: string;
    filterQueryParam: string;
    productSelection: ProductSelection;
    cancelSelection: () => void;
};

export function useAddToGroupBulk({
    sourceProductGroupId,
    filterQueryParam,
    productSelection,
    cancelSelection
}: AddToGroupBulk) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    return async (targetProductGroupId: number) => {
        let requestdata = {
            pathParams: {},
            data: {}
        };

        if (nothingSelected(productSelection)) {
            throw new Error(t('notifications.nothingSelected.error'));
        }

        try {
            requestdata = {
                pathParams: {
                    productGroupId: targetProductGroupId
                },
                data: {
                    ...requestdata.data,
                    amount: 1,
                    source: SOURCE_PRODUCT_GROUPS,
                    sourceId: sourceProductGroupId
                }
            };

            if (isIncludeProductSelection(productSelection)) {
                requestdata = {
                    ...requestdata,
                    pathParams: { ...requestdata.pathParams, kind: 'ids' },
                    data: {
                        ...requestdata.data,
                        lineItemKeys: productSelection.included
                    }
                };
            } else if (isExcludeProductSelection(productSelection)) {
                requestdata = {
                    ...requestdata,
                    pathParams: { ...requestdata.pathParams, kind: 'filter' },
                    data: {
                        ...requestdata.data,
                        filterRequest: deserializeFilter(filterQueryParam),
                        excludedLineItemKeys: productSelection.excluded
                    }
                };
            }
        } catch (error) {
            dispatch(showNotification(t('marketing.productGroups.warnings.noSelectedProducts'), TYPE_WARNING));
            return;
        }

        // workaround to enable quickfilter without error
        // connect handles quickfilters as collectionQuickfilters catman as quickFilters
        const hasQuickfilters = Object.prototype.hasOwnProperty.call(
            requestdata.data['filterRequest'] ?? {},
            'collectionQuickfilters'
        );
        if (requestdata.data['filterRequest'] && hasQuickfilters) {
            requestdata.data['filterRequest']['quickfilters'] =
                requestdata.data['filterRequest']['collectionQuickfilters'];
            delete requestdata.data['filterRequest']['collectionQuickfilters'];
        }

        try {
            await http.put(
                `${shopPrefix}/v1/librarymanaged/productgroups/:productGroupId/bulk/productgroup/:kind/copy`,
                requestdata.data,
                { pathParams: requestdata.pathParams }
            );

            cancelSelection();
            dispatch(showNotification(t('marketing.productGroups.warnings.addSelectedProducts')));
        } catch (error) {
            dispatch(failSelectionlists(error));
        }
    };
}
